import { Button, EmptyState, Table, useDrawer } from '@iveco-ti/iveco-ds'
import { Plus, ShieldBan } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { toast } from 'react-toastify'

import { ExportDropdown } from '@/common/components/ExportDropdown'
import { FilterButton } from '@/common/components/FilterButton'
import { OrderByDropdown } from '@/common/components/OrderByDropdown'
import { Toast } from '@/common/components/Toast'
import { ROUTES } from '@/common/constants/routes'
import { useDownloadExport } from '@/common/hooks/useDownloadExport'
import { usePermissions } from '@/common/hooks/usePermissions'
import { ApplicationTableRow } from './components/ApplicationTableRow'
import { DrawerEditApplication } from './components/DrawerEditApplication'
import { DrawerFilterApplications } from './components/DrawerFilterApplications'
import { DrawerManageApiUsage } from './components/DrawerManageApiUsage'
import { DrawerManageAuth } from './components/DrawerManageAuth'
import { DrawerManageEntityAccount } from './components/DrawerManageEntityAccount'
import { DrawerNewApplication } from './components/DrawerNewApplication'
import { DrawerViewApplication } from './components/DrawerViewApplication'
import { TableRowSkeleton } from './components/TableRowSkeleton'
import { usePaginateApplications } from './hooks/usePaginateApplications'

export default function Applications() {
  const { t } = useTranslation(['sso', 'common'])
  const {
    applications,
    applicationsPaginated,
    isLoadingApplications,
    applicationsBadges,
    applicationsOrderBy,
    applicationsPerPage,
    filtersQty,
    handleApplicationsSearch,
    handleChangePage,
    handleChangeOrder,
  } = usePaginateApplications()
  const { downloadExport, isDownloadingExport } = useDownloadExport({
    route: '/application/report',
  })
  const drawerFilterApplications = useDrawer('drawer-filter-applications')
  const drawerNewApplication = useDrawer('drawer-new-application')
  const { cannot } = usePermissions()

  const ORDER_BY_OPTIONS = [
    {
      label: t('sso:applications.table.order_dropdown.fields.name.label'),
      field: 'title',
    },
  ]

  const TABLE_COLUMNS = [
    {
      label: t('sso:applications.table.table_head.column_first.label'),
      order: 'title',
      badge: applicationsBadges?.applicationKey,
    },
    {
      label: t('sso:applications.table.table_head.column_second.label'),
    },
    {
      label: t('sso:applications.table.table_head.column_third.label'),
    },
    {
      label: t('sso:applications.table.table_head.column_fourth.label'),
      end: true,
    },
  ]

  if (cannot('access', 'Applications')) {
    toast(Toast, {
      toastId: 'applications_access_denied',
      data: {
        type: 'danger',
        icon: ShieldBan,
        title: t('common:access_denied.title'),
        description: t('common:access_denied.description'),
      },
    })

    return <Navigate to={ROUTES.PANEL.NEWS} replace />
  }

  return (
    <>
      <div className="overflow-y-auto h-full shadow-sm rounded-xl bg-white">
        <HeadProvider>
          <Title>{t('sso:applications.browser.title')}</Title>
        </HeadProvider>

        <Table.Root>
          <Table.Search
            searchName="search"
            searchPlaceholder={t(
              'common:authenticated_layout.search_input.placeholder',
            )}
            onSearch={handleApplicationsSearch}
          >
            <OrderByDropdown
              selected={applicationsOrderBy}
              onChangeOrder={handleChangeOrder}
              options={ORDER_BY_OPTIONS}
            />

            <ExportDropdown
              onExportCSV={() => downloadExport({}, 'csv')}
              onExportXLSX={() => downloadExport({}, 'xlsx')}
              isExporting={isDownloadingExport}
            />

            <FilterButton
              open={drawerFilterApplications.open}
              filters={filtersQty}
            />

            <Button.Root onClick={drawerNewApplication.open}>
              <Button.Icon icon={Plus} />
            </Button.Root>
          </Table.Search>

          {isLoadingApplications && <TableRowSkeleton />}
          {!applications.length && !isLoadingApplications && (
            <EmptyState
              title={t('sso:applications.empty_state.title')}
              description={t('sso:applications.empty_state.description')}
            />
          )}

          <Table.Content>
            {!isLoadingApplications && !!applications.length && (
              <>
                <Table.Head
                  hideCheckbox
                  cols={TABLE_COLUMNS}
                  onOrderBy={(order) => handleChangeOrder(order, 'asc')}
                />

                <Table.Body>
                  {applications.map((application) => (
                    <ApplicationTableRow
                      key={application.id}
                      application={application}
                    />
                  ))}
                </Table.Body>
              </>
            )}
          </Table.Content>

          <Table.Footer
            page={applicationsPaginated?.currentPage || 1}
            totalItems={applicationsPaginated?.totalItems || 0}
            totalItemsLabel={t(
              'sso:applications.table.table_footer.total_registers.label',
            )}
            totalPages={applicationsPaginated?.totalPages || 1}
            ofText={t('common:components.table_footer.pagination.label')}
            onChangePage={handleChangePage}
            itemsPerPage={applicationsPerPage}
          />
        </Table.Root>
      </div>

      <DrawerFilterApplications />
      <DrawerNewApplication />
      <DrawerViewApplication />
      <DrawerEditApplication />
      <DrawerManageAuth />
      <DrawerManageApiUsage />
      <DrawerManageEntityAccount />
    </>
  )
}
